import React, { useState } from 'react';
import { cn } from '../../utils/cn';
import { Sidebar, ResponsiveSidebar, MobileHeader } from '../Navigation/Sidebar';

export const DashboardLayout = React.forwardRef(({
  className,
  children,
  user,
  activeItem,
  navigationItems = [],
  onNavigate,
  sidebarCollapsed = false,
  onSidebarToggle,
  ...props
}, ref) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div ref={ref} className={cn("min-h-screen bg-gray-50 dark:bg-dark-50", className)} {...props}>
      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <Sidebar
          user={user}
          activeItem={activeItem}
          navigationItems={navigationItems}
          onNavigate={onNavigate}
          collapsed={sidebarCollapsed}
          onToggleCollapse={onSidebarToggle}
        />
      </div>

      {/* Mobile sidebar */}
      <div className={cn(
        "fixed inset-0 flex z-40 md:hidden",
        mobileMenuOpen ? "block" : "hidden"
      )}>
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* Sidebar */}
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-dark-200 pt-5 pb-4">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <i className="fa-solid fa-xmark text-white text-lg" />
            </button>
          </div>
          <Sidebar
            user={user}
            activeItem={activeItem}
            navigationItems={navigationItems}
            onNavigate={onNavigate}
            className="h-full"
          />
        </div>
      </div>

      {/* Main content */}
      <div className={cn("md:pl-64 flex flex-col flex-1", sidebarCollapsed && "md:pl-16")}>
        {/* Mobile header */}
        <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-50 dark:bg-dark-50 border-b border-gray-200 dark:border-dark-300">
          <MobileHeader
            title="Dashboard"
            user={user}
            onMenuToggle={() => setMobileMenuOpen(true)}
          />
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
});

DashboardLayout.displayName = "DashboardLayout";

// Dashboard Page Container
export const DashboardContainer = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => {
  return (
    <div 
      ref={ref}
      className={cn("max-w-7xl mx-auto px-4 sm:px-6 lg:px-8", className)}
      {...props}
    >
      {children}
    </div>
  );
});

DashboardContainer.displayName = "DashboardContainer";

// Dashboard Header Component
export const DashboardHeader = React.forwardRef(({
  className,
  title,
  subtitle,
  actions,
  backButton,
  onBack,
  ...props
}, ref) => {
  return (
    <div 
      ref={ref}
      className={cn("flex items-center justify-between mb-8", className)}
      {...props}
    >
      <div className="flex items-center space-x-4">
        {backButton && (
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-600 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-dark-100"
          >
            <i className="fa-solid fa-arrow-left text-lg" />
          </button>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-900">
            {title}
          </h1>
          {subtitle && (
            <p className="text-gray-600 dark:text-dark-600 mt-1">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      
      {actions && (
        <div className="flex items-center space-x-3">
          {actions}
        </div>
      )}
    </div>
  );
});

DashboardHeader.displayName = "DashboardHeader";

// Stats Grid Component
export const StatsGrid = React.forwardRef(({
  className,
  children,
  cols = 4,
  ...props
}, ref) => {
  const getGridCols = () => {
    switch (cols) {
      case 1: return "grid-cols-1";
      case 2: return "grid-cols-1 md:grid-cols-2";
      case 3: return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
      case 4: return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
      case 5: return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5";
      case 6: return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6";
      default: return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
    }
  };

  return (
    <div 
      ref={ref}
      className={cn("grid gap-6", getGridCols(), className)}
      {...props}
    >
      {children}
    </div>
  );
});

StatsGrid.displayName = "StatsGrid";

// Charts Grid Component
export const ChartsGrid = React.forwardRef(({
  className,
  children,
  cols = 2,
  ...props
}, ref) => {
  const getGridCols = () => {
    switch (cols) {
      case 1: return "grid-cols-1";
      case 2: return "grid-cols-1 lg:grid-cols-2";
      case 3: return "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3";
      default: return "grid-cols-1 lg:grid-cols-2";
    }
  };

  return (
    <div 
      ref={ref}
      className={cn("grid gap-6", getGridCols(), className)}
      {...props}
    >
      {children}
    </div>
  );
});

ChartsGrid.displayName = "ChartsGrid"; 